import { ErrorApp, traducirErrorSupabase } from '../../domain/errores';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';

/**
 * Descripción de una entidad del inventario.
 *
 * Cada una vive en una tabla distinta, se lee de una vista distinta y usa un
 * nombre distinto para su clave. Declararlo aquí permite que una sola
 * implementación sirva para todas.
 */
export interface DefinicionEntidad {
  /** Tabla sobre la que se escribe. */
  tabla: string;
  /** Origen de lectura; si se omite, se lee de la propia tabla. */
  vista?: string;
  /** Columna que identifica la fila. No siempre se llama `id`. */
  clave: string;
  /** Columna por la que se ordena el listado. */
  orden: string;
}

export type NombreEntidad = 'clientes' | 'areas' | 'nodos' | 'sensores' | 'actuadores';

export const ENTIDADES: Record<NombreEntidad, DefinicionEntidad> = {
  clientes: { tabla: 'clientes', clave: 'id', orden: 'nombre' },
  areas: { tabla: 'areas', clave: 'id', orden: 'nombre' },
  nodos: { tabla: 'nodos', vista: 'vw_nodos_info', clave: 'nodo_id', orden: 'nodo_nombre' },
  sensores: {
    tabla: 'sensores',
    vista: 'vw_sensores_detallado',
    clave: 'id',
    orden: 'nombre_sensor',
  },
  actuadores: {
    tabla: 'actuadores',
    vista: 'v3_vw_actuadores_control_ui',
    clave: 'id',
    orden: 'nombre',
  },
};

/** Fila cruda del inventario, sin traducir a entidad de dominio. */
export type FilaInventario = Record<string, unknown>;

/**
 * Acceso genérico a las tablas del inventario.
 *
 * Estas pantallas son formularios sobre tablas: no tienen reglas de negocio
 * propias más allá de las que ya impone la base. Una sola implementación
 * parametrizada evita cinco repositorios que dirían lo mismo con otros nombres.
 */
export class RepositorioInventario {
  async listar(
    entidad: NombreEntidad,
    filtros: Record<string, string | number | null | undefined> = {},
  ): Promise<FilaInventario[]> {
    const definicion = ENTIDADES[entidad];
    const origen = definicion.vista ?? definicion.tabla;
    let consulta = filtroDemo.aplicar(origen, supabase.from(origen).select('*'));

    // El ámbito de la sesión acota qué clientes se ven; en la propia tabla de
    // clientes la columna es su id.
    consulta = ambitoSesion.aplicar(consulta, entidad === 'clientes' ? 'id' : 'cliente_id');

    for (const [columna, valor] of Object.entries(filtros)) {
      if (valor === null || valor === undefined || valor === '') continue;
      consulta = consulta.eq(columna, valor);
    }

    const { data, error } = await consulta.order(definicion.orden, { ascending: true });
    if (error) throw traducirErrorSupabase(error);

    return (data ?? []) as FilaInventario[];
  }

  async crear(entidad: NombreEntidad, valores: FilaInventario): Promise<void> {
    // Los actuadores no se insertan directos: los crea una función del
    // servidor que valida el código de envío y deja el equipo bien formado.
    // Es el mismo contrato que usaba la versión anterior, y las políticas de
    // la base cuentan con él.
    if (entidad === 'actuadores') {
      const { error } = await supabase.rpc('v2_create_actuador', {
        p_nodo_id: valores.nodo_id,
        p_tipo_id: valores.tipo_id,
        p_nombre: valores.nombre,
        p_codigo_envio: valores.codigo_envio,
        p_safe_state: null,
        p_ttl_s: null,
      });

      if (error) {
        const texto = `${error.code ?? ''} ${error.message ?? ''}`.toUpperCase();
        if (texto.includes('CODIGO_ENVIO_DUPLICATE')) {
          throw new ErrorApp(
            'CONFLICTO',
            'Ya existe un actuador con ese código de envío en este nodo. Usa otro código.',
            error,
          );
        }
        throw traducirErrorSupabase(error);
      }
      return;
    }

    const { error } = await supabase.from(ENTIDADES[entidad].tabla).insert(valores);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizar(
    entidad: NombreEntidad,
    id: string | number,
    valores: FilaInventario,
  ): Promise<void> {
    const definicion = ENTIDADES[entidad];
    const { error } = await supabase
      .from(definicion.tabla)
      .update(valores)
      .eq(definicion.clave, id);

    if (error) throw traducirErrorSupabase(error);
  }

  async eliminar(entidad: NombreEntidad, id: string | number): Promise<void> {
    const definicion = ENTIDADES[entidad];
    const { error } = await supabase.from(definicion.tabla).delete().eq(definicion.clave, id);
    if (error) throw traducirErrorSupabase(error);
  }
}

export const repositorioInventario = new RepositorioInventario();
